/**
 * Sign-in with Google through cf-auth. The access token stays in memory (and a same-origin
 * cookie so thumbnail <img> requests are authenticated). The refresh token stays in localStorage.
 */

import { useSyncExternalStore } from 'react';

const REFRESH_KEY = 'ple:refresh';
const REFRESH_SKEW_MS = 60_000;

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isAuthError(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

export class Forbidden extends Error {
  readonly email: string | null;

  constructor(email: string | null) {
    super('forbidden');
    this.name = 'Forbidden';
    this.email = email;
  }
}

export interface AuthConfig {
  authBase: string;
  googleClientId: string | null;
  /** false only for a local EDITOR_OPEN dev server */
  authRequired?: boolean;
}

export interface SessionAccount {
  id: string;
  email: string | null;
  isAdmin: boolean;
  access: 'admin' | 'member';
}

type SessionEvent = 'signed-out' | 'forbidden';

interface TokenBody {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

let config: AuthConfig | null = null;
let accessToken: string | null = null;
let refreshToken: string | null = null;
let accessUntil = 0;
let account: SessionAccount | null = null;
let refreshing: Promise<boolean> | null = null;

const storeListeners = new Set<() => void>();
const eventListeners = new Set<(event: SessionEvent) => void>();

function emitStore() {
  for (const fn of storeListeners) fn();
}

function emitEvent(event: SessionEvent) {
  for (const fn of eventListeners) fn(event);
}

export function subscribeSession(fn: () => void): () => void {
  storeListeners.add(fn);
  return () => storeListeners.delete(fn);
}

export function getSession(): SessionAccount | null {
  return account;
}

export function useSession(): SessionAccount | null {
  return useSyncExternalStore(subscribeSession, getSession, getSession);
}

export function onSessionEvent(fn: (event: SessionEvent) => void): () => void {
  eventListeners.add(fn);
  return () => eventListeners.delete(fn);
}

export function authConfig(): AuthConfig | null {
  return config;
}

function writeCookie(token: string | null, maxAge: number) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  if (!token) {
    document.cookie = `ple_access=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    return;
  }
  document.cookie = `ple_access=${encodeURIComponent(token)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function clearLocal() {
  accessToken = null;
  refreshToken = null;
  accessUntil = 0;
  account = null;
  localStorage.removeItem(REFRESH_KEY);
  writeCookie(null, 0);
  emitStore();
}

function applyTokens(body: TokenBody) {
  accessToken = body.access_token;
  refreshToken = body.refresh_token;
  accessUntil = Date.now() + body.expires_in * 1000;
  localStorage.setItem(REFRESH_KEY, body.refresh_token);
  writeCookie(body.access_token, body.expires_in);
}

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === 'string') return body.error;
  } catch {
    /* not json */
  }
  return `${res.status}`;
}

export async function loadConfig(): Promise<AuthConfig> {
  const res = await fetch('/api/auth/config');
  if (!res.ok) throw new ApiError(res.status, 'config');
  config = (await res.json()) as AuthConfig;
  return config;
}

async function refresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    if (!refreshToken || !config) return false;
    const res = await fetch(`${config.authBase}/v1/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      clearLocal();
      return false;
    }
    applyTokens((await res.json()) as TokenBody);
    return true;
  })();
  try {
    return await refreshing;
  } finally {
    refreshing = null;
  }
}

async function ensureFresh(): Promise<void> {
  if (!refreshToken) refreshToken = localStorage.getItem(REFRESH_KEY);
  if (accessToken && accessUntil - Date.now() > REFRESH_SKEW_MS) return;
  await refresh();
}

function devUserHeader(): string | null {
  if (config?.authRequired !== false) return null;
  return new URLSearchParams(location.search).get('dev') === '2' ? '2' : null;
}

export async function authorizedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  await ensureFresh();
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  const devUser = devUserHeader();
  if (devUser) headers.set('x-dev-user', devUser);
  let res = await fetch(url, { ...init, headers });
  if (res.status === 401 && (await refresh())) {
    const retry = new Headers(init.headers);
    if (accessToken) retry.set('authorization', `Bearer ${accessToken}`);
    const devUser = devUserHeader();
    if (devUser) retry.set('x-dev-user', devUser);
    res = await fetch(url, { ...init, headers: retry });
  }
  if (res.status === 401) {
    clearLocal();
    emitEvent('signed-out');
  } else if (res.status === 403) {
    const body = (await res.clone().json().catch(() => null)) as { error?: unknown } | null;
    if (body?.error === 'no_access') emitEvent('forbidden');
  }
  return res;
}

export async function apiJson<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await authorizedFetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function loadMe(): Promise<SessionAccount> {
  const res = await authorizedFetch('/api/auth/me');
  if (res.status === 403) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown; account?: { email?: unknown } };
    if (body.error === 'no_access') {
      const email = typeof body.account?.email === 'string' ? body.account.email : null;
      throw new Forbidden(email);
    }
  }
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  const body = (await res.json()) as {
    account: { id: string; email: string | null; isAdmin: boolean };
    access: 'admin' | 'member';
  };
  account = { ...body.account, access: body.access };
  emitStore();
  return account;
}

/** Used when the local server is started with EDITOR_OPEN=1. Production always requires Google. */
export async function enterOpenSession(): Promise<SessionAccount> {
  const devUser = new URLSearchParams(location.search).get('dev') === '2' ? '2' : null;
  const res = await fetch('/api/auth/me', { headers: devUser ? { 'x-dev-user': devUser } : undefined });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  const body = (await res.json()) as {
    account: { id: string; email: string | null; isAdmin: boolean };
    access: 'admin' | 'member';
  };
  account = { ...body.account, access: body.access };
  emitStore();
  return account;
}

export async function restore(): Promise<SessionAccount | null> {
  refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;
  if (!(await refresh())) return null;
  return loadMe();
}

export async function signInWithCredential(credential: string): Promise<SessionAccount> {
  if (!config) throw new Error('auth_not_configured');
  const res = await fetch(`${config.authBase}/v1/google/callback`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  applyTokens((await res.json()) as TokenBody);
  return loadMe();
}

export async function signOut(): Promise<void> {
  const token = refreshToken;
  const base = config?.authBase;
  clearLocal();
  emitEvent('signed-out');
  if (token && base) {
    await fetch(`${base}/v1/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: token }),
    }).catch(() => undefined);
  }
}

export async function searchCfAccounts(q: string): Promise<{ id: string; email: string | null; providers: string[]; created_at: string }[]> {
  if (!config) throw new Error('auth_not_configured');
  await ensureFresh();
  if (!accessToken) throw new ApiError(401, 'unauthorized');
  const url = new URL('/v1/admin/accounts', config.authBase);
  url.searchParams.set('q', q);
  const res = await fetch(url, { headers: { authorization: `Bearer ${accessToken}` } });
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  const body = (await res.json()) as { accounts: { id: string; email: string | null; providers: string[]; created_at: string }[] };
  return body.accounts;
}

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleId {
  initialize(config: { client_id: string; callback: (resp: GoogleCredentialResponse) => void }): void;
  renderButton(parent: HTMLElement, options: { theme?: string; size?: string; text?: string; width?: number }): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
  }
}

let googleScript: Promise<void> | null = null;

function loadGoogle(): Promise<void> {
  if (window.google?.accounts.id) return Promise.resolve();
  if (googleScript) return googleScript;
  googleScript = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('google_script'));
    document.head.appendChild(script);
  });
  return googleScript;
}

/** Renders a Sign In With Google button into parent. */
export async function renderGoogleButton(parent: HTMLElement, onCredential: (credential: string) => void): Promise<void> {
  if (!config?.googleClientId) throw new Error('missing_google_client_id');
  await loadGoogle();
  const id = window.google?.accounts.id;
  if (!id) throw new Error('google_script');
  id.initialize({ client_id: config.googleClientId, callback: (resp) => onCredential(resp.credential) });
  parent.replaceChildren();
  id.renderButton(parent, { theme: 'filled_black', size: 'large', text: 'signin_with', width: 280 });
}
